import React, { useContext, useEffect, useState } from "react";
import { Button, message, Select, Space } from "antd";
import CustomTable from "../../components/CustomTable";
import { ReportView, ReportReply } from "./ReportView";
import useApi from "../../hooks/useApi";
import useFilter from "../../hooks/useFilter";
import { AppStateContext } from "../../context/AppContext";
import {
  ListReport,
  ListClass,
  ListBranch,
  updateReport,
} from "../../constants/API/API";
import { EyeOutlined, SendOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Severty, ShowToast } from "../../helper/toast";
import moment from "moment";

const STATUS = [
  { value: "open", label: "Open" },
  { value: "in-progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const Report = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Report Management");

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [viewModal, setViewModal] = useState(false);
  const [replyModal, setReplyModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [studentList, setStudentList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [branchList, setBranchList] = useState([]);

  const [filters, setFilters] = useFilter({
    sortField: "createdOn",
    sort: "desc",
    studentId: undefined,
    classId: undefined,
    branchId: undefined,
    Status: undefined,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classRes, branchRes] = await Promise.all([
          ListClass(),
          ListBranch(),
        ]);

        setClassList(classRes?.data?.list || []);
        setBranchList(branchRes?.data?.list || []);
      } catch (error) {
        setStudentList([]);
        setClassList([]);
        setBranchList([]);
      }
    };
    fetchData();
  }, []);

  const handleViewClick = (record) => {
    setSelectedReport(record);
    setViewModal(true);
  };

  const handleReplyClick = (record) => {
    // debugger
    setSelectedReport(record);
    setReplyModal(true);
  };

  useApi({
    callback: ListReport,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ docs, totalDocs }) => {
      setData(docs || []);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleFilterChange = (key) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleChangeStatus = async (report, status, reason) => {
    console.log("Status change for report:", report._id, "to", status);
    setLoading(true);
    try {
      await updateReport({ status: status, reply: reason }, report?._id);
      message.success(`Report status updated to ${status} successfully`);
      setRefresh((x) => !x);
      setLoading(false);
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const handleExcelExport = () => {
    if (!data || data.length === 0) {
      ShowToast("No data available to export", Severty.WARNING);
      return;
    }

    // Define the CSV headers based on the table columns
    const headers = [
      "Report ID",
      "User",
      "Student",
      "Branch",
      "Subject",
      "Description",
      "Status",
      "Reported At",
    ];

    // Map the data to CSV rows
    const csvData = data.map((record) => [
      record.issueId || "-",
      record.userId?.name || "-",
      record.studentName || "-",
      record.branchId?.name || "-",
      record.subject || "-",
      record.description || "-",
      record.status || "-",
      record.createdAt ? new Date(record.createdAt).toLocaleDateString() : "-",
    ]);

    // Combine headers and data
    const csvContent = [
      headers.join(","), // Header row
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")), // Data rows with quotes to handle commas
    ].join("\n");

    // Create a Blob and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "reports.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    ShowToast("Report exported successfully", Severty.SUCCESS);
  };

  const handlePdfExport = () => {
    if (!data || data.length === 0) {
      ShowToast("No data available to export", Severty.WARNING);
      return;
    }
    const doc = new jsPDF();

    const headers = [
      [
        "Report ID",
        "User",
        "Student",
        "Branch",
        "Subject",
        "Description",
        "Status",
        "Reported At",
      ],
    ];

    const rows = data.map((record) => [
      record.issueId || "-",
      record.userId?.name || "-",
      record.studentName || "-",
      record.branchId?.name || "-",
      record.subject || "-",
      record.description || "-",
      record.status || "-",
      record.createdAt ? new Date(record.createdAt).toLocaleDateString() : "-",
    ]);

    doc.setFontSize(16);
    doc.text("Report File", 14, 15);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 20,
      styles: { fontSize: 8 },
    });

    // Save the PDF
    doc.save("reports.pdf");

    ShowToast("PDF exported successfully", Severty.SUCCESS);
  };

  const renderFilters = () => (
    <>
      {/* <div className="role-wrap">
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Class"
          onChange={handleFilterChange("classId")}
          value={filters.classId}
          options={classList.map((cls) => ({
            label: cls.name,
            value: cls._id,
          }))}
        />
      </div>
      <div className="role-wrap">
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Branch"
          onChange={handleFilterChange("branchId")}
          value={filters.branchId}
          options={branchList.map((branch) => ({
            label: branch.name,
            value: branch._id,
          }))}
        />
      </div>
      <div className="role-wrap">
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Status"
          onChange={handleFilterChange("Status")}
          value={filters.Status}
        >
          <Select.Option value="">All Status</Select.Option>
          {STATUS.map((status) => (
            <Select.Option key={status.value} value={status.value}>
              {status.label}
            </Select.Option>
          ))}
        </Select>
      </div> */}
      <div className="role-wrap">
        <Button type="primary" onClick={handlePdfExport}>
          Export Report
        </Button>
      </div>
    </>
  );

  const columns = [
    {
      title: "Report ID",
      dataIndex: "issueId",
      key: "issueId",
    },
    {
      title: "User Name",
      dataIndex: "userId",
      key: "userId",
      render: (text, record) => record?.userId?.name || "-",
    },
    {
      title: "Branch Name",
      dataIndex: "branchId",
      key: "branchId",
      render: (text, record) => record?.branchId?.name || "-",
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Reported At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (text ? moment(text).format("ll") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          {/* View Button */}
          <Button
            type="primary"
            onClick={() => handleViewClick(record)}
            icon={<EyeOutlined />}
            title="View"
          />
          <Button
            type="primary"
            onClick={() => handleReplyClick(record)}
            icon={<SendOutlined />}
            title="Reply"
          />

          {/* Change Status Dropdown with Reason */}
          <Select
            style={{ width: 110 }}
            placeholder="Change Status"
            onChange={(status) => {
              handleChangeStatus(record, status);
            }}
            defaultValue={record?.status}
            options={[
              { value: "in-progress", label: "In Progress" },
              { value: "resolved", label: "Resolved" },
              { value: "closed", label: "Closed" },
            ]}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["name"]}
        searchPlaceholder={"Search by User Name"}
        customFilter={renderFilters}
        hideAddButton
      />
      {viewModal && (
        <ReportView
          viewVisible={viewModal}
          setViewVisible={setViewModal}
          selectedReport={selectedReport?._id}
        />
      )}

      {replyModal && (
        <ReportReply
          replyModal={replyModal}
          refresh={() => setRefresh((x) => !x)}
          setReplyModal={setReplyModal}
          report={selectedReport?._id}
        />
      )}
    </>
  );
};

export default Report;
